import { ReactNode } from 'react';
import { AreaChart, LineChart, Radar, ScatterChart } from 'lucide-react';
import { IconType } from 'react-icons';
import {
    TbAlignJustified,
    TbArrowsExchange,
    TbBolt,
    TbBuilding,
    TbBuildingBank,
    TbCalendarStats,
    TbCash,
    TbChartArcs3,
    TbChartDonut4,
    TbChartHistogram,
    TbChartPie,
    TbChartSankey,
    TbCirclesRelation,
    TbCubeSpark,
    TbCurrencyBitcoin,
    TbCurrencyDollar,
    TbDashboard,
    TbDatabaseSearch,
    TbDeviceDesktop,
    TbDeviceMobile,
    TbGrain,
    TbGrid4X4,
    TbLeaf,
    TbMap,
    TbMathXDivideY2,
    TbNumbers,
    TbPercentage,
    TbScale,
    TbSchool,
    TbSeparator,
    TbSourceCode,
    TbSparkles,
    TbSwitchVertical,
    TbTable,
    TbTableOptions,
    TbTypography,
    TbWorld,
    TbZoomInArea,
    TbZoomMoney,
} from 'react-icons/tb';

import {
    ArcBrowserSVGIcon,
    BingSVGIcon,
    BraveSVGIcon,
    ChromeSVGIcon,
    DuckDuckGoSVGIcon,
    EdgeSVGIcon,
    FirefoxSVGIcon,
    OperaSVGIcon,
    SafariSVGIcon,
} from '@/assets';
import { BillingInterval, FeatureAddOnKey, PlanKey } from '@/constants';
import { WORKSPACE_RESOURCE_ICON_REGISTRY, WorkspaceResourceGlyph } from '@/shared/components/workspace-resources';
import { ReportComponentType, UserPlan } from '@/types';
import { DataSourceType } from '@/types/api/modules/business-intelligence';

import type { MapToken } from './types';

export const createReportBillingSessionMaps = (token: MapToken) => {
    const reportComponentTypeToIconMap = {
        [ReportComponentType.TITLE]: <TbTypography size='75%' style={{ color: token.colorTextTertiary }} />,
        [ReportComponentType.DESCRIPTION]: <TbAlignJustified size='75%' style={{ color: token.colorTextTertiary }} />,
        [ReportComponentType.DIVIDER]: <TbSeparator size='75%' style={{ color: token.colorTextTertiary }} />,
        [ReportComponentType.STATISTIC]: <TbNumbers size='75%' style={{ color: token.colorTextTertiary }} />,
        [ReportComponentType.MULTIPLE_STATISTICS]: (
            <TbTableOptions size='75%' style={{ color: token.colorTextTertiary }} />
        ),
        [ReportComponentType.TABLE]: <TbTable size='75%' style={{ color: token.colorTextTertiary }} />,
        [ReportComponentType.MAP]: <TbMap size='75%' style={{ color: token.colorTextTertiary }} />,
        [ReportComponentType.GLOBE]: <TbWorld size='75%' style={{ color: token.colorTextTertiary }} />,
        [ReportComponentType.FUNNEL_CHART]: (
            <svg
                xmlns='http://www.w3.org/2000/svg'
                width='75%'
                height='75%'
                style={{ color: token.colorTextTertiary }}
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                className='icon icon-tabler icons-tabler-outline icon-tabler-chart-funnel'
            >
                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                <path d='M4.387 3h15.226a1 1 0 0 1 .948 1.316l-5.105 15.316a2 2 0 0 1 -1.898 1.368h-3.116a2 2 0 0 1 -1.898 -1.368l-5.104 -15.316a1 1 0 0 1 .947 -1.316' />
                <path d='M5 9h14' />
                <path d='M7 15h10' />
            </svg>
        ),
        [ReportComponentType.SANKEY_CHART]: <TbChartSankey size='75%' style={{ color: token.colorTextTertiary }} />,
        [ReportComponentType.HEAT_MAP]: <TbGrid4X4 size='75%' style={{ color: token.colorTextTertiary }} />,
        [ReportComponentType.SCATTER_PLOT]: <ScatterChart size='0.875rem' style={{ color: token.colorTextTertiary }} />,
        [ReportComponentType.BAR_CHART]: (
            <svg
                xmlns='http://www.w3.org/2000/svg'
                style={{ verticalAlign: '-0.2125rem', color: token.colorTextTertiary }}
                width='0.875rem'
                height='0.875rem'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                className='lucide lucide-chart-column-stacked'
            >
                <path d='M11 13H7' />
                <path d='M19 9h-4' />
                <path d='M3 3v16a2 2 0 0 0 2 2h16' />
                <rect x='15' y='5' width='4' height='12' rx='1' />
                <rect x='7' y='8' width='4' height='9' rx='1' />
            </svg>
        ),
        [ReportComponentType.GAUGE_CHART]: <TbDashboard size='75%' style={{ color: token.colorTextTertiary }} />,
        [ReportComponentType.DONUT_CHART]: <TbChartDonut4 size='75%' style={{ color: token.colorTextTertiary }} />,
        [ReportComponentType.PIE_CHART]: <TbChartPie size='75%' style={{ color: token.colorTextTertiary }} />,
        [ReportComponentType.AREA_CHART]: <AreaChart size='0.875rem' style={{ color: token.colorTextTertiary }} />,
        [ReportComponentType.LINE_CHART]: <LineChart size='0.875rem' style={{ color: token.colorTextTertiary }} />,
        [ReportComponentType.RADAR_CHART]: <Radar size='0.875rem' style={{ color: token.colorTextTertiary }} />,
        [ReportComponentType.RADIAL_CHART]: <TbChartArcs3 size='0.875rem' style={{ color: token.colorTextTertiary }} />,
        [ReportComponentType.CHORD_CHART]: <TbChartDonut4 size='0.875rem' style={{ color: token.colorTextTertiary }} />,
        [ReportComponentType.COUNTERCYCLICAL_VANTAGE]: (
            <TbSourceCode size='75%' style={{ color: token.colorTextTertiary }} />
        ),
    } as Partial<Record<ReportComponentType, ReactNode>>;

    const relationsDashboardsMaps = { reportComponentTypeToIconMap };

    const planIconMap = {
        Plus: <TbSparkles size='1rem' style={{ verticalAlign: '-0.175rem', marginRight: '0.325rem' }} />,
        Pro: <TbBolt size='1rem' style={{ verticalAlign: '-0.175rem', marginRight: '0.325rem' }} />,
        Premium: <TbCubeSpark size='1rem' style={{ verticalAlign: '-0.175rem', marginRight: '0.325rem' }} />,
        Education: <TbSchool size='1rem' style={{ verticalAlign: '-0.175rem', marginRight: '0.325rem' }} />,
        Enterprise: <TbBuilding size='1rem' style={{ verticalAlign: '-0.175rem', marginRight: '0.325rem' }} />,
    } as Record<string, ReactNode>;

    const planKeyIconToMap = {
        plus: TbSparkles,
        pro: TbBolt,
        premium: TbCubeSpark,
        enterprise: TbBuilding,
    } as Record<PlanKey, IconType>;

    const featureAddOnKeyIconToMap = {
        lifecycles: WORKSPACE_RESOURCE_ICON_REGISTRY.lifecycle,
        relations: TbCirclesRelation,
    } as Record<FeatureAddOnKey, IconType>;

    const mapToUserPlan = (plan: PlanKey, interval: BillingInterval): UserPlan => {
        const map: Record<PlanKey, Record<BillingInterval, UserPlan>> = {
            plus: {
                monthly: UserPlan.PLUS_MONTHLY,
                annual: UserPlan.PLUS_ANNUALLY,
            },
            pro: {
                monthly: UserPlan.PRO_MONTHLY,
                annual: UserPlan.PRO_ANNUALLY,
            },
            premium: {
                monthly: UserPlan.PREMIUM_MONTHLY,
                annual: UserPlan.PREMIUM_ANNUALLY,
            },
            enterprise: {
                monthly: UserPlan.ENTERPRISE,
                annual: UserPlan.ENTERPRISE,
            },
        };

        return map[plan][interval];
    };

    const billingMaps = { planIconMap, planKeyIconToMap, featureAddOnKeyIconToMap, mapToUserPlan };

    const browserEngineToSVGMap = {
        Chrome: ChromeSVGIcon,
        Firefox: FirefoxSVGIcon,
        Safari: SafariSVGIcon,
        Arc: ArcBrowserSVGIcon,
        Brave: BraveSVGIcon,
        Opera: OperaSVGIcon,
        Edge: EdgeSVGIcon,
        Bing: BingSVGIcon,
        DuckDuckGo: DuckDuckGoSVGIcon,
    } as Record<string, string>;

    const applicationTypeToIconMap = {
        Web: <TbWorld size='75%' style={{ color: token.colorTextTertiary }} />,
        Desktop: <TbDeviceDesktop size='75%' style={{ color: token.colorTextTertiary }} />,
        Mobile: <TbDeviceMobile size='75%' style={{ color: token.colorTextTertiary }} />,
    } as Record<string, ReactNode>;

    const sessionsMaps = { browserEngineToSVGMap, applicationTypeToIconMap };

    const reportComponentConfigurationDatasetSourceTypeIconMap = {
        [DataSourceType.PLATFORM_FINANCIAL]: <TbZoomMoney style={{ color: token.colorTextTertiary }} size='75%' />,
        [DataSourceType.PLATFORM_ECONOMIC]: <TbWorld style={{ color: token.colorTextTertiary }} size='75%' />,
        [DataSourceType.GLOBAL_TRADE]: <TbWorld style={{ color: token.colorTextTertiary }} size='75%' />,
        [DataSourceType.WORKSPACE_ANALYTICS]: <TbZoomInArea style={{ color: token.colorTextTertiary }} size='75%' />,
        [DataSourceType.THIRD_PARTY]: <TbDatabaseSearch style={{ color: token.colorTextTertiary }} size='75%' />,
    } as Record<DataSourceType, ReactNode>;

    const reportComponentConfigurationDatasetCategoryIconMap = {
        balanceSheet: <TbScale style={{ color: token.colorTextTertiary }} size='75%' />,
        incomeStatement: <TbSwitchVertical style={{ color: token.colorTextTertiary }} size='75%' />,
        cashFlows: <TbCash style={{ color: token.colorTextTertiary }} size='75%' />,
        ratios: <TbPercentage style={{ color: token.colorTextTertiary }} size='75%' />,
        returns: <TbPercentage style={{ color: token.colorTextTertiary }} size='75%' />,
        multiples: <TbMathXDivideY2 style={{ color: token.colorTextTertiary }} size='75%' />,
        activities: <TbCalendarStats style={{ color: token.colorTextTertiary }} size='75%' />,
        commodities: <TbGrain style={{ color: token.colorTextTertiary }} size='75%' />,
        currencies: <TbCurrencyDollar style={{ color: token.colorTextTertiary }} size='75%' />,
        cryptocurrencies: <TbCurrencyBitcoin style={{ color: token.colorTextTertiary }} size='75%' />,
        treasury: <TbBuildingBank style={{ color: token.colorTextTertiary }} size='75%' />,
        economicIndicators: <TbChartHistogram style={{ color: token.colorTextTertiary }} size='75%' />,
        forex: <TbArrowsExchange style={{ color: token.colorTextTertiary }} size='75%' />,
        esgInvestments: <TbLeaf style={{ color: token.colorTextTertiary }} size='75%' />,
        economicCommodities: <TbGrain style={{ color: token.colorTextTertiary }} size='75%' />,
        economicCurrencies: <TbCurrencyDollar style={{ color: token.colorTextTertiary }} size='75%' />,
        economicCryptocurrencies: <TbCurrencyBitcoin style={{ color: token.colorTextTertiary }} size='75%' />,
        economicTreasury: <TbBuildingBank style={{ color: token.colorTextTertiary }} size='75%' />,
        economicForex: <TbArrowsExchange style={{ color: token.colorTextTertiary }} size='75%' />,
        economicEsgInvestments: <TbLeaf style={{ color: token.colorTextTertiary }} size='75%' />,
        folders: <WorkspaceResourceGlyph color={token.colorTextTertiary} resource='folders' />,
        investments: <WorkspaceResourceGlyph color={token.colorTextTertiary} resource='investment' />,
        memos: <WorkspaceResourceGlyph color={token.colorTextTertiary} resource='memo' />,
        pipelines: <WorkspaceResourceGlyph color={token.colorTextTertiary} resource='pipeline' />,
        dashboards: <WorkspaceResourceGlyph color={token.colorTextTertiary} resource='dashboard' />,
        reports: <WorkspaceResourceGlyph color={token.colorTextTertiary} resource='report' />,
        lists: <WorkspaceResourceGlyph color={token.colorTextTertiary} resource='list' />,
        listViews: <WorkspaceResourceGlyph color={token.colorTextTertiary} resource='listView' size='75%' />,
        tasks: <WorkspaceResourceGlyph color={token.colorTextTertiary} resource='task' />,
        resources: <WorkspaceResourceGlyph color={token.colorTextTertiary} resource='resource' />,
        users: <WorkspaceResourceGlyph color={token.colorTextTertiary} resource='member' />,
        teams: <WorkspaceResourceGlyph color={token.colorTextTertiary} resource='team' />,
    } as Record<string, ReactNode>;

    const editReportComponentModalMaps = {
        reportComponentConfigurationDatasetSourceTypeIconMap,
        reportComponentConfigurationDatasetCategoryIconMap,
    };

    return {
        ...relationsDashboardsMaps,
        ...billingMaps,
        ...sessionsMaps,
        ...editReportComponentModalMaps,
    };
};
